import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
  InfoWindow,
  Polyline,
} from "@react-google-maps/api";
import config from "../../config.js";
import {
  normalizeDeviceData,
  DirectionsCache,
  createCacheKey,
} from "../dashboard/mapConstants.js";
import {
  createParentVehicleStream,
  fetchLatestVehiclePosition,
  requestParentRouteDirections,
} from "./ParentViewMap.api.js";
import { mapStyles } from "../dashboard/functions.js";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance.js";
import { fitMapToStops } from "../dashboard/mapUtils.js";
import {
  getAttendantIconSVG,
  getDriverIconSVG,
  getDestinationStopIcon,
  getOriginStopIcon,
  getPhoneIconSVG,
} from "../createForm/icons.js";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import MapLegend from "./MapLegend.jsx";

import { calculateMapRotation } from "../driverConsole/commonFunctions.js";
import { styles } from "./ParentView.js";

const libraries = ["places", "directions"];

const VALID_ROUTE_DIRECTIONS = new Set(["source_to_dest", "dest_to_source"]);

const getRouteOrientationStops = (
  stops = [],
  vehicleRouteDirection = "unknown",
  journeyPhase = "UNKNOWN",
) => {
  if (!stops || stops.length < 2) return 0;

  if (VALID_ROUTE_DIRECTIONS.has(vehicleRouteDirection)) {
    return vehicleRouteDirection === "dest_to_source"
      ? { sourceStop: stops[stops.length - 1], destinationStop: stops[0] }
      : { sourceStop: stops[0], destinationStop: stops[stops.length - 1] };
  }

  if (
    journeyPhase === "GOING_TO_DROP" ||
    journeyPhase === "STUDENT_DROPPED"
  ) {
    return { sourceStop: stops[stops.length - 1], destinationStop: stops[0] };
  }

  return { sourceStop: stops[0], destinationStop: stops[stops.length - 1] };
};

const getInitialMapRotation = (
  stops = [],
  vehicleRouteDirection = "unknown",
  journeyPhase = "UNKNOWN",
) => {
  if (!stops || stops.length < 2) return 0;

  const { sourceStop, destinationStop } = getRouteOrientationStops(
    stops,
    vehicleRouteDirection,
    journeyPhase,
  );

  return calculateMapRotation(
    {
      latitude: parseFloat(sourceStop.latitude),
      longitude: parseFloat(sourceStop.longitude),
    },
    {
      latitude: parseFloat(destinationStop.latitude),
      longitude: parseFloat(destinationStop.longitude),
    },
  );
};

const getHeadingDifference = (currentHeading = 0, targetHeading = 0) => {
  const normalizedCurrent = ((currentHeading % 360) + 360) % 360;
  const normalizedTarget = ((targetHeading % 360) + 360) % 360;
  const diff = Math.abs(normalizedCurrent - normalizedTarget);
  return Math.min(diff, 360 - diff);
};

const isSameStop = (leftStop, rightStop) => {
  if (!leftStop || !rightStop) return false;

  if (leftStop.id != null && rightStop.id != null) {
    return leftStop.id === rightStop.id;
  }

  return (
    String(leftStop.latitude) === String(rightStop.latitude) &&
    String(leftStop.longitude) === String(rightStop.longitude)
  );
};

function ParentViewMap({
  stops,
  routes,
  vehicles,
  studentName,
  studentIcon,
  onVehiclePositionUpdate,
  shouldShowVehicle = true,
  displayVehiclePosition = null,
  journeyPhase = "UNKNOWN",
  vehicleRouteDirection = "unknown",
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fullPath, setFullPath] = useState([]);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [currentRouteColor, setCurrentRouteColor] = useState("#4285F4");
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    () => window.innerWidth >= 768,
  );

  // ✅ FIX 2: Moved useState hooks out of the return block — they were misplaced there before
  const initialMapRotation = useMemo(
    () => getInitialMapRotation(stops, vehicleRouteDirection, journeyPhase),
    [stops, vehicleRouteDirection, journeyPhase],
  );
  const [mapRotation, setMapRotation] = useState(initialMapRotation);
  const [isMapReady, setIsMapReady] = useState(false);

  const eventSourceRef = useRef(null);
  const layoutTimeoutRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const alignmentFrameRef = useRef(null);
  const alignmentStableCountRef = useRef(0);
  const targetRotationRef = useRef(initialMapRotation);
  const lastKnownHeadings = useRef({});
  const positionHistoryRef = useRef({});
  const onVehiclePositionUpdateRef = useRef(onVehiclePositionUpdate);
  const directionsLoadedRef = useRef(false);
  const viewportSignatureRef = useRef("");
  const MIN_MOVEMENT_THRESHOLD = 5;
  const MAX_HISTORY_LENGTH = 5;

  const boundsDataRef = useRef({
    stops: [],
    fullPath: [],
    displayVehiclePosition: null,
  });

  useEffect(() => {
    boundsDataRef.current = { stops, fullPath, displayVehiclePosition };
  }, [stops, fullPath, displayVehiclePosition]);

  useEffect(() => {
    targetRotationRef.current = initialMapRotation;
    setMapRotation(initialMapRotation);
  }, [initialMapRotation]);

  useEffect(() => {
    if (!displayVehiclePosition || !fullPath.length || stops.length !== 2) {
      setRoutePaths({ before: [], after: [] });
    }
  }, [displayVehiclePosition, fullPath, stops.length]);

  useEffect(() => {
    if (!selectedVehicle) return;

    const timer = setTimeout(() => {
      setSelectedVehicle(null);
    }, 4000); 

    return () => clearTimeout(timer); 
  }, [selectedVehicle]);

  const stopAlignmentLoop = useCallback(() => {
    if (alignmentFrameRef.current) {
      cancelAnimationFrame(alignmentFrameRef.current);
      alignmentFrameRef.current = null;
    }
    alignmentStableCountRef.current = 0;
  }, []);

  const stopViewportLayoutLoop = useCallback(() => {
    if (layoutTimeoutRef.current) {
      clearTimeout(layoutTimeoutRef.current);
      layoutTimeoutRef.current = null;
    }
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }
  }, []);

  const enforceVerticalAlignment = useCallback(
    (mapInstance, targetRotation, shouldRevealWhenReady = false) => {
      if (!mapInstance || !window.google?.maps) return;

      stopAlignmentLoop();
      if (shouldRevealWhenReady) {
        setIsMapReady(false);
      }

      const alignStep = () => {
        const currentHeading = mapInstance.getHeading?.() ?? 0;
        const headingDifference = getHeadingDifference(
          currentHeading,
          targetRotation,
        );

        if (headingDifference > 1) {
          mapInstance.setHeading(targetRotation);
          alignmentStableCountRef.current = 0;
          alignmentFrameRef.current = requestAnimationFrame(alignStep);
          return;
        }

        alignmentStableCountRef.current += 1;

        if (alignmentStableCountRef.current < 3) {
          alignmentFrameRef.current = requestAnimationFrame(alignStep);
          return;
        }

        alignmentFrameRef.current = null;
        if (shouldRevealWhenReady) {
          setIsMapReady(true);
        }
      };

      alignmentFrameRef.current = requestAnimationFrame(alignStep);
    },
    [stopAlignmentLoop],
  );

  const syncMapRotation = useCallback(
    (mapInstance, shouldRevealWhenReady = false) => {
      if (!mapInstance || !window.google?.maps) return;

      const targetRotation = targetRotationRef.current ?? 0;
      const currentHeading = mapInstance.getHeading?.() ?? 0;

      setMapRotation(targetRotation);

      if (getHeadingDifference(currentHeading, targetRotation) <= 1) {
        if (shouldRevealWhenReady) {
          setIsMapReady(true);
        }
        return;
      }

      mapInstance.setHeading(targetRotation);
      enforceVerticalAlignment(
        mapInstance,
        targetRotation,
        shouldRevealWhenReady,
      );
    },
    [enforceVerticalAlignment],
  );

  const getViewportProfile = useCallback((mapInstance, hasRoutePath) => {
    const mapDiv = mapInstance.getDiv?.();
    const mapWidth = mapDiv?.clientWidth || window.innerWidth;
    const mapHeight = mapDiv?.clientHeight || window.innerHeight;
    const isMobile = mapWidth < 768;

    return {
      isMobile,
      reservedTop: isMobile ? 108 : 160,
      reservedBottom: isMobile ? 110 : 110,
      reservedSide: isMobile ? 20 : 36,
      targetFill: isMobile ? 0.9 : 0.8,
      boundsPadding: {
        top: Math.round(
          mapHeight * (isMobile ? (hasRoutePath ? 0.1 : 0.14) : 0.16),
        ),
        right: Math.round(mapWidth * (isMobile ? 0.08 : 0.06)),
        bottom: Math.round(
          mapHeight * (isMobile ? (hasRoutePath ? 0.05 : 0.08) : 0.16),
        ),
        left: Math.round(mapWidth * (isMobile ? 0.08 : 0.06)),
      },
    };
  }, []);

  const getViewportSignature = useCallback((mapInstance) => {
    const mapDiv = mapInstance?.getDiv?.();
    const mapWidth = Math.round(mapDiv?.clientWidth || window.innerWidth);
    const mapHeight = Math.round(mapDiv?.clientHeight || window.innerHeight);
    const viewportMode = mapWidth < 768 ? "mobile" : "desktop";

    return `${viewportMode}:${mapWidth}x${mapHeight}`;
  }, []);

  const syncViewportMode = useCallback((mapInstance) => {
    const mapDiv = mapInstance?.getDiv?.();
    const mapWidth = Math.round(mapDiv?.clientWidth || window.innerWidth);
    setIsDesktopViewport(mapWidth >= 768);
  }, []);

  const routeStrokeWeights = useMemo(
    () =>
      isDesktopViewport
        ? {
            covered: 4,
            active: 6,
            fallback: 3,
          }
        : {
            covered: 6,
            active: 10,
            fallback: 5,
          },
    [isDesktopViewport],
  );

  const createViewportBounds = useCallback((includeVehicle = false) => {
    if (!window.google?.maps) return null;

    const { stops, fullPath, displayVehiclePosition } = boundsDataRef.current;
    if (!stops || stops.length === 0) return null;

    const bounds = new window.google.maps.LatLngBounds();

    if (fullPath.length > 1) {
      fullPath.forEach((point) => bounds.extend(point));
    }

    stops.forEach((stop) => {
      const lat = parseFloat(stop.latitude);
      const lng = parseFloat(stop.longitude);
      if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
    });

    if (bounds.isEmpty()) {
      return null;
    }

    if (includeVehicle && displayVehiclePosition) {
      bounds.extend(displayVehiclePosition);
    }

    return bounds.isEmpty() ? null : bounds;
  }, []);

  const getBoundsSpan = useCallback((bounds) => {
    if (!bounds) return null;

    const northEast = bounds.getNorthEast?.();
    const southWest = bounds.getSouthWest?.();
    if (!northEast || !southWest) return null;

    return {
      latSpan: Math.abs(northEast.lat() - southWest.lat()),
      lngSpan: Math.abs(northEast.lng() - southWest.lng()),
    };
  }, []);

  const calculateViewportZoomAdjustment = useCallback(
    (mapInstance, includeVehicle = false) => {
      const contentBounds = createViewportBounds(includeVehicle);
      const visibleBounds = mapInstance?.getBounds?.();
      if (!contentBounds || !visibleBounds) return 0;

      const contentSpan = getBoundsSpan(contentBounds);
      const visibleSpan = getBoundsSpan(visibleBounds);
      if (!contentSpan || !visibleSpan) return 0;

      const latFill =
        visibleSpan.latSpan > 0 ? contentSpan.latSpan / visibleSpan.latSpan : 0;
      const lngFill =
        visibleSpan.lngSpan > 0 ? contentSpan.lngSpan / visibleSpan.lngSpan : 0;
      const currentFill = Math.max(latFill, lngFill);

      if (!isFinite(currentFill) || currentFill <= 0) return 0;

      const mapDiv = mapInstance.getDiv?.();
      const mapWidth = mapDiv?.clientWidth || window.innerWidth;
      const mapHeight = mapDiv?.clientHeight || window.innerHeight;
      const profile = getViewportProfile(
        mapInstance,
        boundsDataRef.current.fullPath.length > 1,
      );
      const { reservedTop, reservedBottom, reservedSide, targetFill } = profile;
      const safeVerticalFill = Math.max(
        0.6,
        (mapHeight - reservedTop - reservedBottom) / mapHeight,
      );
      const safeHorizontalFill = Math.max(
        0.6,
        (mapWidth - reservedSide * 2) / mapWidth,
      );
      const effectiveTargetFill = Math.min(
        targetFill,
        Math.min(safeVerticalFill, safeHorizontalFill) - 0.03,
      );

      return Math.max(
        -1.2,
        Math.min(Math.log2(effectiveTargetFill / currentFill), 1.2),
      );
    },
    [createViewportBounds, getBoundsSpan, getViewportProfile],
  );

  const fitBoundsToContent = useCallback(
    (mapInstance, includeVehicle = false) => {
      if (!mapInstance || !window.google?.maps) return;

      const bounds = createViewportBounds(includeVehicle);
      if (!bounds) return;

      const profile = getViewportProfile(
        mapInstance,
        boundsDataRef.current.fullPath.length > 1,
      );
      mapInstance.fitBounds(bounds, profile.boundsPadding);
    },
    [createViewportBounds, getViewportProfile],
  );

  const applyViewportLayout = useCallback(
    (mapInstance, shouldRevealWhenReady = true) => {
      if (!mapInstance || !window.google?.maps) return;

      stopViewportLayoutLoop();
      const targetRotation = getInitialMapRotation(
        stops,
        vehicleRouteDirection,
        journeyPhase,
      );

      setIsMapReady(false);
      viewportSignatureRef.current = getViewportSignature(mapInstance);
      setMapRotation(targetRotation);
      mapInstance.setHeading(targetRotation);
      fitBoundsToContent(mapInstance, false);

      layoutTimeoutRef.current = setTimeout(() => {
        mapInstance.setHeading(targetRotation);
        const currentZoom = mapInstance.getZoom();
        const zoomAdjustment = calculateViewportZoomAdjustment(
          mapInstance,
          false,
        );

        if (currentZoom != null && Math.abs(zoomAdjustment) > 0.01) {
          mapInstance.setZoom(
            Math.min(Math.max(currentZoom + zoomAdjustment, 10), 18),
          );
        }

        syncMapRotation(mapInstance, shouldRevealWhenReady);
        layoutTimeoutRef.current = null;
      }, 260);
    },
    [
      stops,
      vehicleRouteDirection,
      journeyPhase,
      getViewportSignature,
      fitBoundsToContent,
      calculateViewportZoomAdjustment,
      syncMapRotation,
      stopViewportLayoutLoop,
    ],
  );

  const scheduleViewportLayout = useCallback(
    (mapInstance, shouldRevealWhenReady = true, delay = 120) => {
      if (!mapInstance || !window.google?.maps) return;

      stopViewportLayoutLoop();
      resizeTimeoutRef.current = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance, "resize");
        applyViewportLayout(mapInstance, shouldRevealWhenReady);
        resizeTimeoutRef.current = null;
      }, delay);
    },
    [applyViewportLayout, stopViewportLayoutLoop],
  );

  // Update ref when callback changes
  useEffect(() => {
    onVehiclePositionUpdateRef.current = onVehiclePositionUpdate;
  }, [onVehiclePositionUpdate]);

  // Reset directions loaded flag when routes change
  useEffect(() => {
    directionsLoadedRef.current = false;
  }, [routes]);

  // Extract path points from directions
  const extractPathPoints = useCallback((directions) => {
    if (!directions?.routes?.[0]?.legs) return [];
    let points = [];
    directions.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        const path = step.path;
        path.forEach((point) => {
          points.push({
            lat: typeof point.lat === "function" ? point.lat() : point.lat,
            lng: typeof point.lng === "function" ? point.lng() : point.lng,
          });
        });
      });
    });
    return points;
  }, []);

  // Find nearest point on route
  const findNearestPointOnRoute = useCallback((position, pathPoints) => {
    if (!position || !pathPoints || pathPoints.length === 0) return null;

    let closestPoint = null;
    let minDistance = Infinity;
    let routeIndex = -1;

    pathPoints.forEach((point, index) => {
      const distance = calculateDistance(position, {
        lat: point.lat,
        lng: point.lng,
      });

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
        routeIndex = index;
      }
    });

    const MAX_SNAP_DISTANCE = 100;
    if (minDistance <= MAX_SNAP_DISTANCE) {
      return { ...closestPoint, routeIndex };
    }
    return null;
  }, []);

  // Split route at vehicle position
  const splitRouteAtPosition = useCallback(
    (allPoints, position, journeyPhase) => {
      if (!allPoints.length || !position) {
        return { before: [], after: [] };
      }

      let splitIndex = 0;
      let minDistance = Infinity;

      allPoints.forEach((point, index) => {
        const distance = calculateDistance(position, point);
        if (distance < minDistance) {
          minDistance = distance;
          splitIndex = index;
        }
      });

      const isGoingToSchool =
        journeyPhase === "GOING_TO_SCHOOL" ||
        journeyPhase === "GOING_TO_PICKUP" ||
        journeyPhase === "UNKNOWN";

      if (isGoingToSchool) {
        return {
          before: allPoints.slice(0, splitIndex + 1),
          after: allPoints.slice(splitIndex),
        };
      } else {
        return {
          before: allPoints.slice(splitIndex),
          after: allPoints.slice(0, splitIndex + 1),
        };
      }
    },
    [],
  );

  // Fetch initial vehicle location and set up SSE
  useEffect(() => {
    if (!vehicles || vehicles.length === 0) return;

    const vehicle = vehicles[0];
    const deviceId = vehicle.device_id;
    let isActive = true;

    if (!deviceId) return;

    setIsLoading(false);

    const processVehicleUpdate = (locationPayload) => {
      const normalizedData = normalizeDeviceData(locationPayload);

      if (
        !normalizedData.deviceId ||
        normalizedData.latitude == null ||
        normalizedData.longitude == null
      ) {
        return;
      }

      const lat = parseFloat(normalizedData.latitude);
      const lng = parseFloat(normalizedData.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      if (!positionHistoryRef.current[normalizedData.deviceId]) {
        positionHistoryRef.current[normalizedData.deviceId] = [];
      }

      positionHistoryRef.current[normalizedData.deviceId].push({
        lat,
        lng,
        timestamp: Date.now(),
      });

      const history = positionHistoryRef.current[normalizedData.deviceId];
      if (history.length > MAX_HISTORY_LENGTH) {
        positionHistoryRef.current[normalizedData.deviceId] =
          history.slice(-MAX_HISTORY_LENGTH);
      }

      let heading = lastKnownHeadings.current[normalizedData.deviceId] || 0;

      if (history.length >= 2) {
        const prevPos = history[history.length - 2];
        const currPos = history[history.length - 1];
        const distMoved = calculateDistance(prevPos, currPos);

        if (distMoved != null && distMoved > MIN_MOVEMENT_THRESHOLD) {
          heading = calculateBearing(
            { latitude: prevPos.lat, longitude: prevPos.lng },
            { latitude: currPos.lat, longitude: currPos.lng },
          );
          lastKnownHeadings.current[normalizedData.deviceId] = heading;
        }
      }

      setVehicleLocations([
        {
          ...normalizedData,
          latitude: lat,
          longitude: lng,
          heading,
          name: vehicle.name,
          drivers: vehicle.drivers,
        },
      ]);

      if (onVehiclePositionUpdateRef.current) {
        onVehiclePositionUpdateRef.current({ lat, lng });
      }
    };

    const setupSSE = () => {
      const eventSource = createParentVehicleStream(deviceId);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "connected") {
            // console.log("SSE connected");
            return;
          }

          if (!isActive) {
            return;
          }
          processVehicleUpdate(data);
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE connection error:", error);
        eventSource.close();
      };
    };

    const initializeVehicleTracking = async () => {
      try {
        const initialPosition = await fetchLatestVehiclePosition(deviceId);

        if (isActive && initialPosition?.latitude && initialPosition?.longitude) {
          // console.log("Initial DB position:", initialPosition);
          processVehicleUpdate({
            ...initialPosition,
            deviceId,
            device_id: deviceId,
          });
        }
      } catch (error) {
        console.error("Error fetching initial DB position:", error);
      } finally {
        if (isActive) {
          setupSSE();
        }
      }
    };

    initializeVehicleTracking();

    return () => {
      isActive = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [vehicles]);

  // Fetch and render directions
  useEffect(() => {
    if (
      !isLoaded ||
      !routes ||
      routes.length === 0 ||
      !map ||
      directionsLoadedRef.current
    ) {
      return;
    }

    const route = routes[0];
    const cacheKey = createCacheKey(route, stops);

    const cachedDirections = DirectionsCache.get(cacheKey);

    if (cachedDirections) {
      const color =
        route.routeColor || cachedDirections.routeColor || "#4285F4";
      setCurrentRouteColor(color);
      setDirections([{ ...cachedDirections, routeColor: color }]);
      const pathPoints = extractPathPoints(cachedDirections);
      setFullPath(pathPoints);
      directionsLoadedRef.current = true;
      return;
    }

    const fetchDirections = async () => {
      const routeCoords = route.routeCoordinates.sort(
        (a, b) => a.order - b.order,
      );
      let filteredCoords = routeCoords;

      if (stops.length === 2) {
        const pickupStop = stops[0];
        const dropStop = stops[1];

        const pickupIndex = routeCoords.findIndex(
          (coord) =>
            Math.abs(
              parseFloat(coord.latitude) - parseFloat(pickupStop.latitude),
            ) < 0.0001 &&
            Math.abs(
              parseFloat(coord.longitude) - parseFloat(pickupStop.longitude),
            ) < 0.0001,
        );

        const dropIndex = routeCoords.findIndex(
          (coord) =>
            Math.abs(
              parseFloat(coord.latitude) - parseFloat(dropStop.latitude),
            ) < 0.0001 &&
            Math.abs(
              parseFloat(coord.longitude) - parseFloat(dropStop.longitude),
            ) < 0.0001,
        );

        if (pickupIndex !== -1 && dropIndex !== -1) {
          const startIndex = Math.min(pickupIndex, dropIndex);
          const endIndex = Math.max(pickupIndex, dropIndex);
          filteredCoords = routeCoords.slice(startIndex, endIndex + 1);
        }
      }

      if (filteredCoords.length < 2) {
        console.error("Route needs at least 2 coordinates");
        setIsMapReady(true);
        return;
      }

      const origin = new window.google.maps.LatLng(
        parseFloat(filteredCoords[0].latitude),
        parseFloat(filteredCoords[0].longitude),
      );

      const destination = new window.google.maps.LatLng(
        parseFloat(filteredCoords[filteredCoords.length - 1].latitude),
        parseFloat(filteredCoords[filteredCoords.length - 1].longitude),
      );

      const waypoints = filteredCoords.slice(1, -1).map((coord) => ({
        location: new window.google.maps.LatLng(
          parseFloat(coord.latitude),
          parseFloat(coord.longitude),
        ),
        stopover: coord.isStop || false,
      }));

      try {
        const result = await requestParentRouteDirections({
          googleMaps: window.google.maps,
          origin,
          destination,
          waypoints,
        });
        const routeColor = route.routeColor || route.color || "#4285F4";
        setCurrentRouteColor(routeColor);
        DirectionsCache.set(cacheKey, result, routeColor);
        setDirections([{ ...result, routeColor }]);
        const pathPoints = extractPathPoints(result);
        setFullPath(pathPoints);
        directionsLoadedRef.current = true;
      } catch (error) {
        console.error(error.message);
      }
    };

    fetchDirections();
  }, [isLoaded, routes, map, stops, extractPathPoints]);

  // Update route paths based on vehicle position
  useEffect(() => {
    if (!displayVehiclePosition || !fullPath.length || stops.length !== 2)
      return;

    const split = splitRouteAtPosition(
      fullPath,
      displayVehiclePosition,
      journeyPhase,
    );

    if (split.before.length > 0 || split.after.length > 0) {
      setRoutePaths(split);
    }
  }, [
    displayVehiclePosition,
    fullPath,
    stops,
    journeyPhase,
    splitRouteAtPosition,
  ]);

  // ✅ FIX 3: Apply map rotation based on first and last stop — same logic as DriverSpecificMap
  useEffect(() => {
    if (map && stops && stops.length >= 2) {
      const rotation = getInitialMapRotation(
        stops,
        vehicleRouteDirection,
        journeyPhase,
      );

      setMapRotation(rotation);
      map.setHeading(rotation);
      syncMapRotation(map, false);
      // console.log("ParentViewMap rotation applied:", rotation, "degrees");
    }
  }, [map, stops, vehicleRouteDirection, journeyPhase, syncMapRotation]);

  // ✅ FIX 4: onLoad applies heading immediately and listens to zoom changes
  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      setIsMapReady(false);
      viewportSignatureRef.current = getViewportSignature(mapInstance);
      syncViewportMode(mapInstance);

      if (stops && stops.length >= 2) {
        boundsDataRef.current = { stops, fullPath, displayVehiclePosition };
        scheduleViewportLayout(
          mapInstance,
          !routes || routes.length === 0,
          80,
        );
      }

      if ((!stops || stops.length < 2) && (!routes || routes.length === 0)) {
        setIsMapReady(true);
      }

      const handleMapSettled = () => {
        syncMapRotation(mapInstance, false);
      };

      const idleListener = mapInstance.addListener("idle", handleMapSettled);
      const tilesLoadedListener = mapInstance.addListener(
        "tilesloaded",
        handleMapSettled,
      );

      // ✅ Re-fit on window resize (handles mobile ↔ desktop switching)
      const handleViewportChange = () => {
        const nextSignature = getViewportSignature(mapInstance);

        if (!nextSignature || nextSignature === viewportSignatureRef.current) {
          return;
        }

        viewportSignatureRef.current = nextSignature;
        syncViewportMode(mapInstance);
        scheduleViewportLayout(mapInstance, true, 140);
      };
      window.addEventListener("resize", handleViewportChange);
      window.addEventListener("orientationchange", handleViewportChange);

      let resizeObserver = null;
      const mapDiv = mapInstance.getDiv?.();
      if (mapDiv && typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          handleViewportChange();
        });
        resizeObserver.observe(mapDiv);
      }

      mapInstance._resizeCleanup = () => {
        window.removeEventListener("resize", handleViewportChange);
        window.removeEventListener("orientationchange", handleViewportChange);
        resizeObserver?.disconnect();
        window.google?.maps?.event?.removeListener(idleListener);
        window.google?.maps?.event?.removeListener(tilesLoadedListener);
      };
    },
    [
      stops,
      fullPath,
      displayVehiclePosition,
      getViewportSignature,
      routes,
      scheduleViewportLayout,
      syncViewportMode,
      syncMapRotation,
    ],
  );

  // Re-fit bounds once route directions are rendered
  useEffect(() => {
    if (
      !map ||
      !isLoaded ||
      !window.google?.maps ||
      directions.length === 0 ||
      stops.length === 0
    )
      return;

    boundsDataRef.current = { stops, fullPath, displayVehiclePosition };
    scheduleViewportLayout(map, true, 120);

    return () => {
      stopViewportLayoutLoop();
    };
  }, [
    map,
    directions,
    fullPath,
    stops,
    scheduleViewportLayout,
    stopViewportLayoutLoop,
  ]);

  // Inject custom CSS to override Google InfoWindow default styles
  useEffect(() => {
    const styleId = "custom-infowindow-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
  /* Main container - remove Google's reserved top space */
  .gm-style .gm-style-iw-c {
    background: #E8EAEE !important;
    border-radius: 16px !important;
    padding: 0 !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45) !important;
    border: 0.5px solid #C8CBD0 !important;
    max-width: 320px !important;
  }

  /* Kill the top gap Google adds for the × button */
  .gm-style .gm-style-iw-d {
    overflow: hidden !important;
    padding: 0 !important;
    margin-top: 0 !important;
  }

  /* Tail / arrow */
  .gm-style .gm-style-iw-t::after {
    background: #1a1f2e !important;
    box-shadow: none !important;
  }

  /* ✅ Key fix: push × down to sit beside the title text */
  .gm-style .gm-style-iw-c button.gm-ui-hover-effect {
    position: absolute !important;
    top: 14px !important;
    right: 12px !important;
    width: 22px !important;
    height: 22px !important;
    background: rgba(255, 255, 255, 0.15) !important;
    border-radius: 50% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    opacity: 1 !important;
    margin: 0 !important;
  }

  .gm-style .gm-style-iw-c button.gm-ui-hover-effect img,
  .gm-style .gm-style-iw-c button.gm-ui-hover-effect span {
    opacity: 0.85 !important;
    width: 12px !important;
    height: 12px !important;
    margin: 0 !important;
  }
`;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  const onUnmount = useCallback(() => {
    if (map?._resizeCleanup) {
      map._resizeCleanup();
    }
    stopViewportLayoutLoop();
    stopAlignmentLoop();
    setIsMapReady(false);
    setMap(null);
  }, [map, stopViewportLayoutLoop, stopAlignmentLoop]);

  const vehicleLegendIconUrl = useMemo(() => {
    if (studentIcon) return studentIcon;
    const svg = getRightToLeftBusIcon("green", 180).trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, [studentIcon]);

  const routeLegendColors = useMemo(
    () => directions.map((direction) => direction.routeColor || "#4285F4"),
    [directions],
  );

  const stopLegendItems = useMemo(() => {
    if (!isLoaded || !window.google?.maps) {
      return [
        { label: "Origin", color: "#20A8E0" },
        { label: "Destination", color: "#EE3B2B" },
      ];
    }
    return [
      { label: "Origin", iconUrl: getOriginStopIcon()?.url },
      { label: "Destination", iconUrl: getDestinationStopIcon()?.url },
    ];
  }, [isLoaded]);

  const routeOrientationStops = useMemo(() => {
    if (!stops || stops.length < 2) {
      return { sourceStop: null, destinationStop: null };
    }

    return getRouteOrientationStops(stops, vehicleRouteDirection, journeyPhase);
  }, [stops, vehicleRouteDirection, journeyPhase]);

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div style={styles.loadingWrapper}>
        <div>Loading map...</div>
      </div>
    );
  }

  return (
    <div style={styles.mapContainer}>
      {!isMapReady && (
        <div style={styles.mapLoaderOverlay}>
          <div>Loading map...</div>
        </div>
      )}
      <GoogleMap
        mapContainerStyle={{
          ...styles.fullContainer,
          opacity: isMapReady ? 1 : 0,
          transition: "opacity 180ms ease",
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          heading: mapRotation,
          mapId: "a7388450e4f50eab65a0b0ff",
          mapTypeId: "terrain",
          tilt: 45, // ✅ FIX 6: was 220 — valid range is 0–45
          mapTypeControl: true,
          streetViewControl: true,
          rotateControl: false,
          fullscreenControl: false,
          scrollwheel: true,
          gestureHandling: "cooperative",
          minZoom: 10,
          maxZoom: 20,
          disableDefaultUI: false,
          zoomControl: true,
        }}
      >
        {/* Render Route with Path Splitting */}
        {shouldShowVehicle &&
        routePaths.before.length > 0 &&
        routePaths.after.length > 0 ? (
          <>
            {/* COVERED PATH - Grey */}
            <Polyline
              path={routePaths.before}
              options={{
                strokeColor: "#808080",
                strokeOpacity: 1.0,
                strokeWeight: routeStrokeWeights.covered,
                zIndex: 1,
              }}
            />
            {/* UNCOVERED PATH - Route Color */}
            <Polyline
              path={routePaths.after}
              options={{
                strokeColor: currentRouteColor,
                strokeOpacity: 1.0,
                strokeWeight: routeStrokeWeights.active,
                zIndex: 2,
              }}
            />
          </>
        ) : (
          directions.map((direction, index) => (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: direction.routeColor || "#4285F4",
                  strokeOpacity: 0.8,
                  strokeWeight: routeStrokeWeights.fallback,
                },
              }}
            />
          ))
        )}

        {/* Render Stops */}
        {stops &&
          stops.length > 0 &&
          stops.map((stop) => {
            const lat = parseFloat(stop.latitude);
            const lng = parseFloat(stop.longitude);

            if (isNaN(lat) || isNaN(lng)) {
              console.warn(`Invalid coordinates for stop ${stop.id}:`, stop);
              return null;
            }

            const isOrigin = isSameStop(stop, routeOrientationStops.sourceStop);
            const stopIcon = isOrigin
              ? getOriginStopIcon()
              : getDestinationStopIcon();

            return (
              <React.Fragment key={stop.id}>
                <OverlayView
                  position={{ lat, lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div style={styles.stopLabelWrapper}>
                    <div
                      style={{
                        ...styles.stopLabel,
                        backgroundColor: isOrigin ? "#20A8E0" : "#EE3B2B",
                        border: isOrigin
                          ? "0.5px solid #20A8E0"
                          : "0.5px solid #EE3B2B",
                      }}
                    >
                      {stop.name || `Stop ${stop.id}`}
                    </div>
                  </div>
                </OverlayView>
                <Marker position={{ lat, lng }} icon={stopIcon} />
              </React.Fragment>
            );
          })}

        {/* Render Student/Vehicle Location */}
        {shouldShowVehicle && displayVehiclePosition && (
          <React.Fragment>
            <OverlayView
              position={displayVehiclePosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onClick={() => {
                  if (selectedVehicle) {
                    setSelectedVehicle(null);
                    return;
                  }
                  if (vehicleLocations.length > 0) {
                    setSelectedVehicle(vehicleLocations[0]);
                  } else if (vehicles && vehicles.length > 0) {
                    setSelectedVehicle(vehicles[0]);
                  }
                }}
                style={styles.vehicleMarker}
              >
                {studentIcon ? (
                  <img
                    src={studentIcon}
                    alt="Student Location"
                    style={styles.studentImage}
                  />
                ) : (
                  <div style={styles.studentInitial}>
                    {(studentName || "S").charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={styles.vehicleLabel}>
                  {studentName || "Your Child"}
                </div>
              </div>
            </OverlayView>

            {selectedVehicle && (
              <InfoWindow
                position={displayVehiclePosition}
                onCloseClick={() => setSelectedVehicle(null)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -70),
                  disableAutoPan: true,
                }}
              >
                <div
                  style={{ padding: "14px 16px 16px 16px", color: "#183059" }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#183059",
                      lineHeight: "22px",
                      paddingRight: "36px",
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      paddingBottom: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    {studentName || "Your Child"}'s Location
                  </div>

                  {/* Driver Section */}
                  <div style={{ ...styles.driverCard, marginBottom: "8px" }}>
                    <div>
                      <img
                        src={getDriverIconSVG().url}
                        alt="Driver"
                        style={styles.driverIcon}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.driverName}>
                        {selectedVehicle.drivers && selectedVehicle.drivers[0]
                          ? selectedVehicle.drivers[0].name
                          : "Driver"}
                      </div>
                      <div style={styles.driverPhone}>
                        {selectedVehicle.drivers &&
                        selectedVehicle.drivers[0]?.phone
                          ? selectedVehicle.drivers[0].phone
                          : "No phone number"}
                      </div>
                    </div>
                    {selectedVehicle.drivers &&
                    selectedVehicle.drivers[0]?.phone ? (
                      <a
                        href={`tel:${selectedVehicle.drivers[0].phone}`}
                        style={styles.callLink}
                      >
                        <img src={getPhoneIconSVG().url} alt="Phone" />
                      </a>
                    ) : (
                      <div style={styles.callLinkDisabled}>
                        <img
                          src={getPhoneIconSVG().url}
                          alt="Phone"
                          style={{ opacity: 0.3 }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Attendant Section */}
                  <div style={styles.driverCard}>
                    <div>
                      <img
                        src={getAttendantIconSVG().url}
                        alt="Attendant"
                        style={styles.driverIcon}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.driverName}>
                        {selectedVehicle.drivers && selectedVehicle.drivers[1]
                          ? selectedVehicle.drivers[1].name
                          : "Attendant"}
                      </div>
                      <div style={styles.driverPhone}>
                        {selectedVehicle.drivers &&
                        selectedVehicle.drivers[1]?.phone
                          ? selectedVehicle.drivers[1].phone
                          : "No phone number"}
                      </div>
                    </div>
                    {selectedVehicle.drivers &&
                    selectedVehicle.drivers[1]?.phone ? (
                      <a
                        href={`tel:${selectedVehicle.drivers[1].phone}`}
                        style={styles.callLink}
                      >
                        <img src={getPhoneIconSVG().url} alt="Phone" />
                      </a>
                    ) : (
                      <div style={styles.callLinkDisabled}>
                        <img
                          src={getPhoneIconSVG().url}
                          alt="Phone"
                          style={{ opacity: 0.3 }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </InfoWindow>
            )}
          </React.Fragment>
        )}

        <MapLegend
          showVehicle={shouldShowVehicle}
          vehicleIconUrl={vehicleLegendIconUrl}
          routeColors={routeLegendColors}
          stopItems={stopLegendItems}
          schoolApp={true}
        />
      </GoogleMap>
    </div>
  );
}

export default React.memo(ParentViewMap);
